import { get } from 'lodash';
import {
    type Control,
    Controller,
    type FieldError,
    type FieldErrors,
    type FieldValues,
    type Path,
    type UseFormRegister,
    useWatch,
} from 'react-hook-form';
import { type StackProps, YStack } from 'tamagui';

import { Checkbox, CheckboxWithLabel } from '../inputs/Checkbox';
import { FootnoteRegular } from '../text/Typography';
import { ERROR_TEXT_COLOR } from './ControlledInput';

interface ControlledCheckboxProps<TFieldValues extends FieldValues, TContext> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    label?: string;
    containerStyle?: StackProps;
    disabled?: boolean;
    size?: 'default' | 'large';
    tone?: 'neutral' | 'positive';
}

export const ControlledCheckbox = <TFieldValues extends FieldValues, TContext>({
    name,
    control,
    register,
    label,
    errors,
    containerStyle,
    ...checkboxProps
}: ControlledCheckboxProps<TFieldValues, TContext>) => {
    // Use lodash get to access nested values in dot notation
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    // Watch the form value for display purposes

    const currentValue = useWatch({ control, name });

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...register,
            }}
            render={({ field: { onChange, value } }) => {
                const isChecked = Boolean(currentValue ?? value);

                const handlePress = () => {
                    // Unlike text inputs (which get new value from event) or pickers (which pass new value directly),
                    // checkboxes need to toggle, requiring the CURRENT value at execution time, not from closure.
                    // field.value can be stale in the closure if handler was created before re-render.
                    // We read from form state at execution time to ensure we always toggle the correct value.
                    const formState = control as {
                        _formValues?: Record<string, unknown>;
                        _getWatch?: (
                            name: Path<TFieldValues>,
                        ) => boolean | undefined;
                    };
                    const actualCurrentValue =
                        formState._getWatch?.(name) ??
                        (formState._formValues?.[name as string] as
                            | boolean
                            | undefined) ??
                        isChecked;

                    onChange(!actualCurrentValue);
                };

                return (
                    <YStack gap={8} {...containerStyle}>
                        {label != null ? (
                            <CheckboxWithLabel
                                id={name}
                                label={label}
                                checked={isChecked}
                                onPress={handlePress}
                                disabled={checkboxProps.disabled}
                                size={checkboxProps.size}
                                tone={checkboxProps.tone}
                            />
                        ) : (
                            <Checkbox
                                id={name}
                                checked={isChecked}
                                onPress={handlePress}
                                disabled={checkboxProps.disabled}
                                size={checkboxProps.size}
                                tone={checkboxProps.tone}
                            />
                        )}
                        {isError && (
                            <FootnoteRegular color={ERROR_TEXT_COLOR}>
                                {error?.message}
                            </FootnoteRegular>
                        )}
                    </YStack>
                );
            }}
        />
    );
};
